import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-xs border px-1.5 py-1 text-xs w-fit whitespace-nowrap shrink-0 [&>svg]:size-4 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-primary bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-secondary bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-destructive bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        tertiary:
          'border-tertiary bg-tertiary text-primary-foreground [a&]:hover:bg-tertiary/90',
        warning:
          'border-warning-accent bg-warning bg-opacity-10 text-warning-accent [a&]:hover:bg-warning/90',
        success:
          'border-success bg-success text-white [a&]:hover:bg-success/90',
      },
      colorScheme: {
        default: '',
        success: '',
        warning: '',
        danger: '',
        neutral: '',
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        colorScheme: 'success',
        className: 'border-success bg-success/5 text-success',
      },
      {
        variant: 'outline',
        colorScheme: 'warning',
        className: 'border-warning bg-warning/5 text-warning',
      },
      {
        variant: 'outline',
        colorScheme: 'danger',
        className: 'border-destructive bg-destructive/5 text-destructive',
      },
      {
        variant: 'outline',
        colorScheme: 'neutral',
        className:
          'border-muted-foreground bg-muted-foreground/5 text-muted-foreground',
      },
    ],
    defaultVariants: {
      variant: 'default',
    },
  },
);

interface BadgeProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof badgeVariants> {
  asChild?: boolean;
}

const Badge = React.forwardRef<HTMLElement, BadgeProps>(
  ({ className, variant, colorScheme, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';

    return (
      <Comp
        ref={ref}
        data-slot="badge"
        className={cn(badgeVariants({ variant, colorScheme }), className)}
        {...props}
      />
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
